import {
	Box,
	Button,
	Chip,
	Container,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useTheme,
	useMediaQuery,
	Collapse,
	IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import React, {useCallback, useEffect, useMemo, useRef, useState, useTransition} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import i18next from '../../lang';
import axios from "axios";
import {rootStore} from "../../stores/RootStore";
import {Category, Product, ProductSet} from "../admin/Product/Interfaces";
import ConfirmDialog from "../../components/ConfirmDialog";
import PurchaseStepper from "../../components/PurchaseStepper";
import {
	DeliveryData,
	UserProductMapping,
	OrderQuantity,
	SelectedCurriculum,
	FilterState
} from './interfaces';

interface LocationState {
	deliveryData?: DeliveryData[];
	prevOrderData?: {
		deliveryData: DeliveryData[];
		selectedCurriculums: SelectedCurriculum[];
		orderQuantities?: OrderQuantity[];
		filters: FilterState;
		userProductMappings?: { [key: string]: UserProductMapping[] };
	};
	tempOrder?: {
		deliveryData: DeliveryData[];
		orderQuantities: OrderQuantity[];
	};
	fromOrderConfirm?: boolean;
	// 前の画面から引き継ぐ一時保存データ存在フラグ
	hasTempOrder?: boolean;
}

const OrderInput: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { viewStatusStore, authStore } = rootStore;
	const [isCancelOrderDialogOpen, setIsCancelOrderDialogOpen] = useState(false);
	const [isCompletedSaveTemporaryOrderDialogOpen, setIsCompletedSaveTemporaryOrderDialogOpen] = useState(false);
	const locationState = location.state as LocationState | null;
	const locationDeliveryData = locationState?.deliveryData;
	const prevOrderData = locationState?.prevOrderData;
	const tempOrder = locationState?.tempOrder;
	// 一時保存データの存在フラグ — tempOrder が直接ある場合か、前の画面から引き継いだフラグのOR
	const hasTempOrder = !!tempOrder || (locationState?.hasTempOrder ?? false);
	const fromOrderConfirm = locationState?.fromOrderConfirm;
	const [products, setProducts] = useState<Product[]>([]);
	const [productSets, setProductSets] = useState<ProductSet[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);
	const [selectedCurriculums, setSelectedCurriculums] = useState<SelectedCurriculum[]>([]);
	const [collapsedCurriculums, setCollapsedCurriculums] = useState<Set<string>>(new Set());
	const curriculumRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
	const [userProductMappings, setUserProductMappings] = useState<{ [key: string]: UserProductMapping[] }>(
		() => prevOrderData?.userProductMappings || {}
	);
	const [isMappingsLoaded, setIsMappingsLoaded] = useState(false);
	const [curriculumDialogOpen, setCurriculumDialogOpen] = useState(false);
	const [isPending, startTransition] = useTransition();
	const [removedCurriculums, setRemovedCurriculums] = useState<SelectedCurriculum[]>([]);
	const [tempOrderStaleDialogOpen, setTempOrderStaleDialogOpen] = useState(false);

	const [deliveryData] = useState<DeliveryData[]>(() => {
		if (locationDeliveryData) return locationDeliveryData;
		if (prevOrderData?.deliveryData) return prevOrderData.deliveryData;
		if (tempOrder?.deliveryData) return tempOrder.deliveryData;
		return [];
	});

	// 配送日時選択画面に戻るときの navigate state を構築する。
	// キャンセル系（handleCancelOrder）は意図的にリセットするため、この関数を使わない。
	const buildDeliveryDateTimeState = () => {
		const validData = deliveryData.filter(d => d.addressId != null);
		return {
			skipTempOrderDialog: true,
			hasTempOrder,
			selectedAddresses: validData.map(d => ({
				// DeliveryAddress.id はユーザーID — DeliveryDateTime の handleNext が userId として使うため
				// String() で型を統一（d.userId は string、d.id は addressId 文字列）
				id: String(d.userId ?? d.id),
				addressId: d.addressId!,
				companyName: d.companyName ?? '',
				brandName: d.brandName ?? '',
				classroomName: d.classroomName,
				aliasName: d.aliasName ?? '',
				address: d.address,
				phone: d.phone,
				userName: d.userName,
				postage: d.postage,
			})),
			prevDeliveryData: Object.fromEntries(
				validData.map(d => [d.addressId!.toString(), {
					date: d.deliveryDate instanceof Date ? d.deliveryDate.toISOString() : d.deliveryDate,
					time: d.deliveryTime
				}])
			)
		};
	};

	const [filters, setFilters] = useState<FilterState>(
		prevOrderData?.filters || {
			categoryId: 0
		}
	);

	useEffect(() => {
		const fetchOrderProducts = async () => {
			await fetchProducts();
			await fetchProductSets();
		};

		fetchCategories();
		fetchOrderProducts();
	}, []);

	useEffect(() => {
		if (deliveryData.length > 0) {
			fetchUserProductMappings();
		}
	}, [deliveryData]);

	useEffect(() => {
		if (products.length > 0 || productSets.length > 0) {
			let updatedCurriculums: SelectedCurriculum[] = [];

			if (prevOrderData?.selectedCurriculums) {
				// prevOrderData에서 하나라도 선택된 커리큘럼이 있다면 해당 커리큘럼의 모든 교재를 표시
				updatedCurriculums = prevOrderData.selectedCurriculums
					.filter(curriculum => curriculum.books.some(book => 
						Object.values(book.quantity).some(qty => qty > 0)
					))
					.map(curriculum => {
						// 해당 커리큘럼의 모든 교재 가져오기
						let allBooks;
						if (curriculum.type === 'normal') {
							allBooks = products
								.filter(product => product.curriculumName === curriculum.name)
								.map(product => {
									const existingBook = curriculum.books.find(b => b.id === product.id);
									return {
										id: product.id,
										title: `${product.curriculumName || ''} ${product.volume || ''} ${product.title || ''}`,
										price: product.price,
										isSelected: true,
										quantity: existingBook?.quantity || 
											Object.keys(curriculum.books[0]?.quantity || {}).reduce((acc, id) => ({
												...acc,
												[id]: 0
											}), {})
									};
								});
						} else {
							allBooks = productSets
								.filter(productSet => productSet.name === curriculum.name)
								.map(productSet => {
									const existingBook = curriculum.books.find(b => b.id === productSet.id);
									return {
										id: productSet.id,
										title: productSet.name,
										price: productSet.price,
										isSelected: true,
										quantity: existingBook?.quantity || 
											Object.keys(curriculum.books[0]?.quantity || {}).reduce((acc, id) => ({
												...acc,
												[id]: 0
											}), {})
									};
								});
						}

						return {
							name: curriculum.name,
							type: curriculum.type,
							books: allBooks
						};
					});
			} else if (tempOrder?.orderQuantities) {
				// 一時保存データの検証: 商品の削除・価格変更チェック
				let tempOrderIsStale = false;
				for (const order of tempOrder.orderQuantities) {
					for (const savedBook of order.books) {
						if (order.type === 'normal') {
							const currentProduct = products.find(p => p.id === savedBook.id);
							if (!currentProduct || currentProduct.price !== savedBook.price) {
								tempOrderIsStale = true;
								break;
							}
						} else {
							const currentSet = productSets.find(ps => ps.id === savedBook.id);
							if (!currentSet || currentSet.price !== savedBook.price) {
								tempOrderIsStale = true;
								break;
							}
						}
					}
					if (tempOrderIsStale) break;
				}

				if (tempOrderIsStale) {
					setTempOrderStaleDialogOpen(true);
					return;
				}

				// 一時保存データ処理
				updatedCurriculums = tempOrder.orderQuantities.map(order => {
					const savedBooksMap = order.books.reduce((acc, book) => {
						acc[book.id] = book.quantity;
						return acc;
					}, {} as { [key: number]: { [key: string]: number } });

					let allBooks;
					if (order.type === 'normal') {
						allBooks = products
							.filter(product => product.curriculumName === order.name)
							.map(product => {
								const savedQuantity = savedBooksMap[product.id];
								return {
									id: product.id,
									title: `${product.curriculumName || ''} ${product.volume || ''} ${product.title || ''}`,
									price: product.price,
									isSelected: true,
									quantity: savedQuantity || Object.keys(order.books[0]?.quantity || {}).reduce((acc, id) => ({
										...acc,
										[id]: 0
									}), {})
								};
							});
					} else {
						allBooks = productSets
							.filter(productSet => productSet.name === order.name)
							.map(productSet => {
								const savedQuantity = savedBooksMap[productSet.id];
								return {
									id: productSet.id,
									title: productSet.name,
									price: productSet.price,
									isSelected: true,
									quantity: savedQuantity || Object.keys(order.books[0]?.quantity || {}).reduce((acc, id) => ({
										...acc,
										[id]: 0
									}), {})
								};
							});
					}

					return {
						name: order.name,
						type: order.type,
						books: allBooks
					};
				});
			}

			setSelectedCurriculums(updatedCurriculums);
			// 첫 번째 교재 그룹만 열고 나머지는 접기
			if (updatedCurriculums.length > 1) {
				setCollapsedCurriculums(new Set(
					updatedCurriculums.slice(1).map(c => `${c.type}-${c.name}`)
				));
			}
		}
	}, [products, productSets, prevOrderData, tempOrder]);

	const fetchCategories = async () => {
		try {
			const response = await axios.get('/api/v1/admin/product-categories');
			setCategories(response.data);
		} catch (error) {
			console.error('Failed to get category list：', error);
		}
	};

	const fetchProducts = async () => {
		try {
			const response = await axios.get('/api/v1/admin/products');
			setProducts(response.data.filter(product => product.status === 'ACTIVE'));
		} catch (error: any) {
			console.error('Failed to fetch products:', error);
		}
	};

	const fetchProductSets = async () => {
		try {
			const response = await axios.get('/api/v1/admin/product-sets');
			setProductSets(response.data);
		} catch (error) {
			console.error('Failed to fetch product sets:', error);
		}
	};

	const fetchUserProductMappings = async () => {
		try {
			const newMappings: { [key: string]: UserProductMapping[] } = {};

			// deliveryData の各住所に対して UserProductMapping API 呼び出し
			// ユーザーIDでの重複取得を避けるため userId (または id) をキーとして使用
			for (const delivery of deliveryData) {
				const userId = delivery.userId ?? delivery.id;
				// 既存のマッピングまたは今回のループで取得済みならスキップ
				if (userProductMappings[userId] || newMappings[userId]) continue;
				try {
					const response = await axios.get(`/api/v1/admin/product-mappings/user/${userId}`);
					newMappings[userId] = response.data;
				} catch (error) {
					console.error(`Failed to fetch user product mappings for user ${userId}:`, error);
					newMappings[userId] = [];
				}
			}

			// 既存マッピングと新規マッピングをマージ
			setUserProductMappings(prev => ({ ...prev, ...newMappings }));
		} catch (error) {
			console.error('Failed to fetch user product mappings:', error);
		} finally {
			setIsMappingsLoaded(true);
		}
	};

	const getFilteredCurriculums = () => {
		let filteredProductCurriculums = [...new Set(products.filter(product => {
			return (!filters.categoryId || product.categoryId === filters.categoryId);
		}).map(product => {
			return {
				type: 'normal',
				name: product.curriculumName
			}
		}))];

		if (!filters.categoryId || filters.categoryId === -1) {
			const filteredProductSetNames = [...new Set(productSets.map(productSet => {
				return {
					type: 'set',
					name: productSet.name
				}
			}))];

			filteredProductCurriculums = [...filteredProductCurriculums, ...filteredProductSetNames];
		}

		const locale = i18next.language || 'ja-JP';
		return [...new Set(filteredProductCurriculums.map(item => `${item.name}-${item.type}`))].map(item => {
			const [name, type] = item.split('-');
			return { name, type };
		}).sort((a, b) => a.name.localeCompare(b.name, locale));
	};

	const handleFilterChange = (field: keyof FilterState, value: number) => {
		setFilters(prev => {
			return {
				...prev,
				[field]: value
			};
		});
	};

	const convertSchoolType = (type: string): string => {
		const schoolTypeMap: { [key: string]: string } = {
			'KINDERGARTEN': i18next.t('product.kindergarten'),
			'ELEMENTARY': i18next.t('product.elementary_school'),
			'MIDDLE': i18next.t('product.middle_school'),
			'HIGH': i18next.t('product.high_school')
		};
		return schoolTypeMap[type] || type;
	};

	const handleCurriculumToggle = (type: string, name: string) => {
		const isAlreadySelected = selectedCurriculums.some(
			curriculum => curriculum.name === name && curriculum.type === type
		);

		if (isAlreadySelected) {
			// 해제 시 입력된 수량 데이터를 보관
			const removedCurriculum = selectedCurriculums.find(
				curriculum => curriculum.name === name && curriculum.type === type
			);
			if (removedCurriculum) {
				setRemovedCurriculums(prev => [
					...prev.filter(c => !(c.name === name && c.type === type)),
					removedCurriculum
				]);
			}
			setSelectedCurriculums(prev =>
				prev.filter(curriculum => !(curriculum.name === name && curriculum.type === type))
			);
			return;
		}

		// 이전에 보관된 데이터가 있으면 복원
		const savedCurriculum = removedCurriculums.find(
			c => c.name === name && c.type === type
		);
		if (savedCurriculum) {
			setRemovedCurriculums(prev => prev.filter(c => !(c.name === name && c.type === type)));
			setSelectedCurriculums(prev => [...prev, savedCurriculum]);
			return;
		}

		const addBooks = type === 'normal' ? products.filter(product => product.curriculumName === name).map(product => {
			return {
				id: product.id,
				title: `${product.curriculumName || ''} ${product.volume || ''} ${product.title || ''}`,
				price: product.price,
				quantity: deliveryData.reduce((acc, curr) => ({...acc, [curr.id]: 0}), {})
			}})

			: productSets.filter(productSet => productSet.name === name).map(productSet => {
				return {
					id: productSet.id,
					title: `${productSet.name}`,
					price: productSet.price,
					quantity: deliveryData.reduce((acc, curr) => ({...acc, [curr.id]: 0}), {})
				}})

		const newCurriculum = {
			name,
			type,
			books: addBooks
		};

		setSelectedCurriculums(prev => [...prev, newCurriculum]);
	};

	const handleSelectAllCurriculums = () => {
		const allCurriculums = getCategorizedCurriculums.purchasable;
		// deliveryData 기반 quantity 초기값을 한번만 계산
		const emptyQuantity = Object.fromEntries(deliveryData.map(d => [d.id, 0]));
		const newCurriculums: SelectedCurriculum[] = [];
		for (const curriculum of allCurriculums) {
			const isAlreadySelected = selectedCurriculums.some(
				s => s.name === curriculum.name && s.type === curriculum.type
			);
			if (!isAlreadySelected) {
				const addBooks = curriculum.type === 'normal'
					? products.filter(p => p.curriculumName === curriculum.name).map(p => ({
						id: p.id,
						title: `${p.curriculumName || ''} ${p.volume || ''} ${p.title || ''}`,
						price: p.price,
						quantity: {...emptyQuantity}
					}))
					: productSets.filter(ps => ps.name === curriculum.name).map(ps => ({
						id: ps.id,
						title: ps.name,
						price: ps.price,
						quantity: {...emptyQuantity}
					}));
				newCurriculums.push({ name: curriculum.name, type: curriculum.type, books: addBooks });
			}
		}
		if (newCurriculums.length > 0) {
			setCurriculumDialogOpen(false);
			startTransition(() => {
				setSelectedCurriculums(prev => [...prev, ...newCurriculums]);
			});
		}
	};

	const handleDeselectAllCurriculums = () => {
		// 전체 해제 시 입력된 수량 데이터를 보관
		setRemovedCurriculums(prev => {
			const updated = [...prev];
			selectedCurriculums.forEach(curriculum => {
				const idx = updated.findIndex(
					c => c.name === curriculum.name && c.type === curriculum.type
				);
				if (idx >= 0) {
					updated[idx] = curriculum;
				} else {
					updated.push(curriculum);
				}
			});
			return updated;
		});
		setSelectedCurriculums([]);
	};

	const handleQuantityChange = (curriculumIndex: number, bookId: number, classroomId: string, value: number) => {
		const clamped = Math.max(0, Math.min(99999, value));
		setSelectedCurriculums(prev => {
			const updated = [...prev];
			const book = updated[curriculumIndex].books.find(b => b.id === bookId);
			if (book) {
				book.quantity[classroomId] = clamped;
			}
			return updated;
		});
	};

	const toggleCurriculumCollapse = (key: string) => {
		setCollapsedCurriculums(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const scrollToCurriculum = (type: string, name: string) => {
		const key = `${type}-${name}`;
		// 접혀 있으면 펼치기
		setCollapsedCurriculums(prev => {
			const next = new Set(prev);
			next.delete(key);
			return next;
		});
		setTimeout(() => {
			curriculumRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 100);
	};

	const handleBookSelection = (curriculumIndex: number, bookId: number, isSelected: boolean) => {
		setSelectedCurriculums(prev => {
			const updated = [...prev];
			const book = updated[curriculumIndex].books.find(b => b.id === bookId);
			if (book) {
				book.isSelected = isSelected;
				if (!isSelected) {
					for (const key in book.quantity) {
						book.quantity[key] = 0;
					}
				}
			}
			return updated;
		});
	};

	const isBookSelected = (book: { quantity: { [key: number]: number } }) => {
		return Object.values(book.quantity).some(value => value > 0);
	};

	// 구매 가능한 상품 룩업 Set (O(1) 검색)
	const availableProductSet = useMemo(() => {
		const set = new Set<string>();
		for (const [userId, mappings] of Object.entries(userProductMappings)) {
			for (const mapping of mappings) {
				if (mapping.productType === 'normal') {
					set.add(`${userId}-normal-${mapping.productId}`);
				} else if (mapping.productType === 'set') {
					set.add(`${userId}-set-${mapping.productSetId}`);
				}
			}
		}
		return set;
	}, [userProductMappings]);

	const isProductAvailableForUser = useCallback((productId: number, productType: string, userId: string) => {
		// 매핑이 아직 로딩 중이면 모든 상품 이용 가능으로 처리
		if (!isMappingsLoaded) return true;
		// 해당 사용자에 대한 매핑이 없으면 모든 상품 이용 가능 (제한 미설정)
		const userMappings = userProductMappings[userId];
		if (!userMappings || userMappings.length === 0) return true;
		return availableProductSet.has(`${userId}-${productType}-${productId}`);
	}, [availableProductSet, isMappingsLoaded, userProductMappings]);

	// 커리큘럼이 구매 가능한지 판정: 그룹 내 하나의 상품이라도 어떤 delivery user에게 구매 가능하면 true
	const isCurriculumPurchasable = useCallback((curriculumName: string, curriculumType: string) => {
		if (!isMappingsLoaded) return true;
		// 모든 delivery user에 대해 매핑이 없으면 (제한 미설정) → 구매 가능
		// userId（商品マッピングのキー）で検索する
		const hasAnyMappings = deliveryData.some(d => {
			const userId = d.userId ?? d.id;
			const m = userProductMappings[userId];
			return m && m.length > 0;
		});
		if (!hasAnyMappings) return true;

		if (curriculumType === 'normal') {
			const curriculumProducts = products.filter(p => p.curriculumName === curriculumName);
			return curriculumProducts.some(product =>
				deliveryData.some(delivery => isProductAvailableForUser(product.id, 'normal', delivery.userId ?? delivery.id))
			);
		} else {
			const curriculumSets = productSets.filter(ps => ps.name === curriculumName);
			return curriculumSets.some(ps =>
				deliveryData.some(delivery => isProductAvailableForUser(ps.id, 'set', delivery.userId ?? delivery.id))
			);
		}
	}, [products, productSets, deliveryData, userProductMappings, isMappingsLoaded, isProductAvailableForUser]);

	// 커리큘럼을 구매 가능/불가능 두 그룹으로 분류
	const getCategorizedCurriculums = useMemo(() => {
		const all = getFilteredCurriculums();
		const purchasable: { name: string; type: string }[] = [];
		const other: { name: string; type: string }[] = [];
		for (const c of all) {
			if (isCurriculumPurchasable(c.name, c.type)) {
				purchasable.push(c);
			} else {
				other.push(c);
			}
		}
		return { purchasable, other };
	}, [products, productSets, filters, userProductMappings, isMappingsLoaded, deliveryData, isCurriculumPurchasable]);

	// 기본값: prevOrderData/tempOrder가 없으면 전체 구매 가능 교재 자동 선택
	useEffect(() => {
		if (prevOrderData?.selectedCurriculums || tempOrder?.orderQuantities) return;
		if (!isMappingsLoaded || (products.length === 0 && productSets.length === 0)) return;
		if (selectedCurriculums.length > 0) return;

		const allCurriculums = getCategorizedCurriculums.purchasable;
		if (allCurriculums.length === 0) return;

		const emptyQuantity = Object.fromEntries(deliveryData.map(d => [d.id, 0]));
		const newCurriculums: SelectedCurriculum[] = allCurriculums.map(curriculum => {
			const books = curriculum.type === 'normal'
				? products.filter(p => p.curriculumName === curriculum.name).map(p => ({
						id: p.id,
						title: `${p.curriculumName || ''} ${p.volume || ''} ${p.title || ''}`,
						price: p.price,
						quantity: { ...emptyQuantity }
					}))
				: productSets.filter(ps => ps.name === curriculum.name).map(ps => ({
						id: ps.id,
						title: ps.name,
						price: ps.price,
						quantity: { ...emptyQuantity }
					}));
			return { name: curriculum.name, type: curriculum.type, books };
		});

		setSelectedCurriculums(newCurriculums);
		// 첫 번째 교재 그룹만 열고 나머지는 접기
		if (newCurriculums.length > 1) {
			setCollapsedCurriculums(new Set(
				newCurriculums.slice(1).map(c => `${c.type}-${c.name}`)
			));
		}
	}, [isMappingsLoaded, products, productSets, getCategorizedCurriculums, deliveryData, prevOrderData, tempOrder, selectedCurriculums.length]);

	useEffect(() => {
		if (!locationState) {
			navigate('/', { replace: true });
		}
	}, [locationState, navigate]);

	if (!locationState) return null;

	const getValidSelectedCurriculums = () => {
		return selectedCurriculums.map(curriculum => ({
			...curriculum,
			books: curriculum.books.filter(book =>
				!Object.values(book.quantity).every(qty => qty === 0)
			)
		})).filter(curriculum => curriculum.books.length > 0)
	}

	const getOrderData = () => {
		// 현재 선택된 커리큘럼 데이터 가져오기
		const currentOrderQuantities = getValidSelectedCurriculums();

		// 이전 주문 데이터와 현재 주문 데이터 머지
		const mergedOrderQuantities = [...(prevOrderData?.orderQuantities || [])];

		// 임시저장 데이터가 있는 경우 해당 데이터도 머지
		if (tempOrder?.orderQuantities) {
			tempOrder.orderQuantities.forEach(tempCurriculum => {
				const existingCurriculumIndex = mergedOrderQuantities.findIndex(
					prev => prev.name === tempCurriculum.name && prev.type === tempCurriculum.type
				);

				if (existingCurriculumIndex === -1) {
					mergedOrderQuantities.push(tempCurriculum);
				} else {
					const existingCurriculum = mergedOrderQuantities[existingCurriculumIndex];
					tempCurriculum.books.forEach(tempBook => {
						const existingBookIndex = existingCurriculum.books.findIndex(
							prev => prev.id === tempBook.id
						);

						if (existingBookIndex === -1) {
							existingCurriculum.books.push(tempBook);
						} else {
							const existingBook = existingCurriculum.books[existingBookIndex];
							existingCurriculum.books[existingBookIndex] = {
								...existingBook,
								quantity: {
									...existingBook.quantity,
									...tempBook.quantity
								}
							};
						}
					});
				}
			});
		}

		currentOrderQuantities.forEach(currentCurriculum => {
			const existingCurriculumIndex = mergedOrderQuantities.findIndex(
				prev => prev.name === currentCurriculum.name && prev.type === currentCurriculum.type
			);

			if (existingCurriculumIndex === -1) {
				mergedOrderQuantities.push(currentCurriculum);
			} else {
				const existingCurriculum = mergedOrderQuantities[existingCurriculumIndex];
				currentCurriculum.books.forEach(currentBook => {
					const existingBookIndex = existingCurriculum.books.findIndex(
						prev => prev.id === currentBook.id
					);

					if (existingBookIndex === -1) {
						existingCurriculum.books.push(currentBook);
					} else {
						const existingBook = existingCurriculum.books[existingBookIndex];
						existingCurriculum.books[existingBookIndex] = {
							...existingBook,
							quantity: {
								...existingBook.quantity,
								...currentBook.quantity
							}
						};
					}
				});
			}
		});

		const allDeliveryData = [
			...(prevOrderData?.deliveryData || []),
			...(tempOrder?.deliveryData || []),
			...deliveryData
		].reduce((unique, item) => {
			const exists = unique.find(x => x.id === item.id);
			if (!exists) {
				unique.push(item);
			}
			return unique;
		}, [] as DeliveryData[]);

		return {
			deliveryData: allDeliveryData,
			orderQuantities: mergedOrderQuantities,
		};
	}

	const handleSubmit = () => {
		const orderData = getOrderData();

		// ブラウザの戻るボタン対応：現在のhistory entryに注文データを保存
		const stateForBack = {
			...locationState,
			prevOrderData: {
				deliveryData,
				selectedCurriculums,
				orderQuantities: orderData.orderQuantities,
				filters
			}
		};
		window.history.replaceState(
			{ ...window.history.state, usr: stateForBack },
			''
		);

		navigate('/purchase/order-confirm', {
			state: {
				orderData,
				products,
				productSets,
				categories,
				userProductMappings,
				hasTempOrder
			}
		});
	};

	const handleCancelOrderWithDeleteTemp = async () => {
		try {
			await axios.delete('/api/v1/orders/temp');
		} catch (error) {
			console.error('Failed to delete temp order:', error);
		}
		handleCancelOrder(true);
	};

	const handleCancelOrder = (tempOrderDeleted = false) => {
		const effectiveHasTempOrder = tempOrderDeleted ? false : hasTempOrder;
		setIsCancelOrderDialogOpen(false);
		if (fromOrderConfirm && prevOrderData) {
			navigate('/purchase/order-confirm', {
				state: {
					orderData: {
						deliveryData: (prevOrderData as any).deliveryData,
						orderQuantities: (prevOrderData as any).orderQuantities
					},
					products: (prevOrderData as any).products,
					productSets: (prevOrderData as any).productSets,
					categories: (prevOrderData as any).categories,
					userProductMappings: (prevOrderData as any).userProductMappings,
					hasTempOrder: effectiveHasTempOrder
				}
			});
		} else if (authStore.user.level === 'CLASSROOM_MANAGER') {
			navigate('/purchase/delivery-datetime', { state: { skipTempOrderDialog: true, hasTempOrder: effectiveHasTempOrder } });
		} else {
			navigate('/purchase/delivery-address', { state: { skipTempOrderDialog: true, hasTempOrder: effectiveHasTempOrder } });
		}
	};

	const handleTemporarySave = async () => {
		try {
			viewStatusStore.setLoadingStatus({ open: true });

			const orderData = getOrderData();
			await axios.post('/api/v1/orders/temp', orderData, {
				headers: {
					'Content-Type': 'application/json'
				}
			});
			setIsCompletedSaveTemporaryOrderDialogOpen(true);
		} catch (error: any) {
			console.error('Failed to save temp order request:', error);
		} finally {
			viewStatusStore.setLoadingStatus({ open: false });
		}
	};

	return (
		<Container maxWidth={false} sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, md: 3 }, width: '100%' }}>
			<style>{`
				input[type="number"]::-webkit-inner-spin-button,
				input[type="number"]::-webkit-outer-spin-button {
					opacity: 1 !important;
					height: 24px;
				}
			`}</style>
			<PurchaseStepper activeStep={2} />
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Box sx={{ mb: 3 }}>
						<Typography variant="h6" gutterBottom>
							{i18next.t('purchase.enter_order_details')}
						</Typography>
						<Grid container spacing={3} sx={{ mb: 2 }}>
							<Grid item xs={12}>
								<Grid container spacing={1} alignItems="center" sx={{ height: '100%' }}>
									<Grid item>
										<Button
											variant="outlined"
											onClick={() => setCurriculumDialogOpen(true)}
											sx={{
												height: '35px',
												fontSize: '0.8rem',
												minWidth: { xs: '100%', md: 200 },
												textTransform: 'none'
											}}
										>
											{i18next.t('product.curriculum_name')}
											{selectedCurriculums.length > 0 && ` (${selectedCurriculums.length})`}
										</Button>
									</Grid>
									{selectedCurriculums.length > 0 && (
										<Grid item xs={12}>
											<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
												{selectedCurriculums.map((curriculum) => (
													<Chip
														key={`selected-${curriculum.type}-${curriculum.name}`}
														label={curriculum.name}
														size="small"
														color="primary"
														variant="filled"
														onClick={() => scrollToCurriculum(curriculum.type, curriculum.name)}
														onDelete={() => handleCurriculumToggle(curriculum.type, curriculum.name)}
														sx={{ fontSize: '0.75rem', cursor: 'pointer' }}
													/>
												))}
											</Box>
										</Grid>
									)}

									{!fromOrderConfirm && (
										<Grid item>
											<Button
												variant="outlined"
												sx={{ height: '35px', fontSize: '0.8rem', minWidth: '80px' }}
												onClick={() => navigate('/purchase/delivery-datetime', { state: buildDeliveryDateTimeState() })}
											>
												{i18next.t('purchase.go_to_delivery_datetime')}
											</Button>
										</Grid>
									)}
									<Grid item sx={{ marginLeft: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
										<Button
											variant="outlined"
											sx={{ height: '35px', fontSize: '0.8rem', minWidth: '80px' }}
											onClick={() => setIsCancelOrderDialogOpen(true)}
										>
											{fromOrderConfirm ? i18next.t('purchase.cancel_and_return_to_order_confirm') : i18next.t('order.order_cancelled')}
										</Button>
										<Button
											variant="outlined"
											sx={{ height: '35px', fontSize: '0.8rem', minWidth: '80px' }}
											onClick={handleTemporarySave}
										>
											{i18next.t('purchase.save_temporarily')}
										</Button>
										<Button
											variant="contained"
											disabled={selectedCurriculums.length === 0 || getValidSelectedCurriculums().length === 0}
											sx={{
												height: '35px',
												fontSize: '0.8rem',
												minWidth: '80px',
												backgroundColor: 'primary.main',
												'&:hover': { backgroundColor: 'primary.dark' }
											}}
											onClick={handleSubmit}
										>
											{i18next.t('purchase.go_to_order_confirm')}
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Box>

					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						mt: '20px'
					}}>
						<Paper sx={{
							display: 'flex',
							flexDirection: 'column',
							boxShadow: 'none',
							border: 'none',
							borderRadius: '4px',
							backgroundColor: '#FFFFF0',
							pl: '20px',
							pt: '16px'
						}}>
							{selectedCurriculums.map((curriculum, curriculumIndex) => {
								const collapseKey = `${curriculum.type}-${curriculum.name}`;
								const isCollapsed = collapsedCurriculums.has(collapseKey);
								return (
								<Box
									key={`${curriculum.type}-${curriculum.name}-${curriculumIndex}`}
									ref={(el: HTMLDivElement | null) => { curriculumRefs.current[collapseKey] = el; }}
									sx={{ mb: 2, borderBottom: isCollapsed ? '1px solid #e0e0e0' : 'none', pb: isCollapsed ? 1 : 0 }}
								>
									<Box
										sx={{ display: 'flex', alignItems: 'center', mb: 1, cursor: 'pointer' }}
										onClick={() => toggleCurriculumCollapse(collapseKey)}
									>
										<Typography variant="h6" sx={{ flexGrow: 1 }}>
											{curriculum.name}
										</Typography>
										<IconButton size="small">
											{isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
										</IconButton>
									</Box>
									{/* 접힌 상태에서도 수량이 있는 교재는 표시 */}
									{isCollapsed && curriculum.books.some(b => Object.values(b.quantity).some(q => q > 0)) && (
										<Box sx={{ pl: '20px', pb: 1 }}>
											{curriculum.books.filter(b => Object.values(b.quantity).some(q => q > 0)).map(book => (
												<Typography key={book.id} variant="body2" sx={{ fontSize: '0.75rem', color: '#555', lineHeight: 1.8 }}>
													{book.title} — {Object.entries(book.quantity).filter(([, q]) => q > 0).map(([id, q]) => {
														const delivery = deliveryData.find(d => d.id === id);
														return `${delivery?.classroomName || id}: ${q}`;
													}).join(', ')}
												</Typography>
											))}
										</Box>
									)}
									<Collapse in={!isCollapsed}>
									<TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
										<Table sx={{
											borderCollapse: 'separate',
											borderSpacing: 0,
											'& .MuiTableCell-root': {
												borderRight: '1px solid #f0f0f0',
												borderBottom: '1px solid #f0f0f0',
												padding: '0 8px',
												height: '40px',
												whiteSpace: 'nowrap'
											}
										}} size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={{
														backgroundColor: '#f5f5f5',
														fontSize: '0.7rem',
														fontWeight: 800,
														width: '160px',
														maxWidth: '180px',
														position: 'sticky',
														left: 0,
														zIndex: 10
													}}>
														<span style={{ fontWeight: 'bold' }}>{i18next.t('order.textbook_name')}</span>
													</TableCell>
													<TableCell sx={{
														backgroundColor: '#f5f5f5',
														fontSize: '0.7rem',
														fontWeight: 800,
														textAlign: 'center',
														width: '70px',
														minWidth: '70px',
													}}>
														<span style={{ fontWeight: 'bold' }}>{i18next.t('order.unit_price')}</span>
													</TableCell>
													{deliveryData.map((delivery, index) => (
														<TableCell key={delivery.id} sx={{
															backgroundColor: '#f5f5f5',
															fontSize: '0.7rem',
															fontWeight: 800,
															textAlign: 'center',
															writingMode: 'vertical-rl',
															minHeight: '80px',
															height: 'auto !important',
															py: 1,
															lineHeight: 1.2
														}}>
															{String.fromCharCode(0x2460 + (prevOrderData ? prevOrderData.deliveryData ? prevOrderData.deliveryData.length : 0 : 0) + index)} {delivery.classroomName}
														</TableCell>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{curriculum.books.map((book) => (
													<TableRow key={book.id} sx={{
														height: '40px !important',
														backgroundColor: isBookSelected(book) ? '#FFFFD0' : '#fff',
														'&:hover': {
															backgroundColor: isBookSelected(book) ? '#FFFFD0' : '#f5f5f5'
														}
													}}>
														<TableCell sx={{
															fontSize: '0.7rem',
															height: '40px !important',
															position: 'sticky',
															left: 0,
															backgroundColor: isBookSelected(book) ? '#FFFFD0' : '#fff',
															'&:hover': {
																backgroundColor: isBookSelected(book) ? '#FFFFD0' : '#f5f5f5'
															},
															zIndex: 5
														}}>
															{book.title}
														</TableCell>
														<TableCell align="right" sx={{
															fontSize: '0.7rem',
															height: '40px !important',
															whiteSpace: 'nowrap',
															color: '#444',
														}}>
															{book.price.toLocaleString()}{i18next.t('common.yen')}
														</TableCell>
														{deliveryData.map((delivery) => {
															const isAvailable = isProductAvailableForUser(book.id, curriculum.type, delivery.userId ?? delivery.id);
															return (
																<TableCell key={delivery.id} align="center" sx={{ height: '40px !important', position: 'relative', zIndex: 0 }}>
																	<input
																		type="number"
																		inputMode="numeric"
																		value={book.quantity[delivery.id] || ''}
																		onChange={(e) => {
																			const val = parseInt(e.target.value) || 0;
																			handleQuantityChange(curriculumIndex, book.id, delivery.id, val);
																		}}
																		disabled={!isAvailable}
																		min={0}
																		max={99999}
																		style={{
																			width: '60px',
																			height: '28px',
																			textAlign: 'center',
																			fontSize: '16px',
																			padding: '4px',
																			border: '1px solid #ccc',
																			borderRadius: '4px',
																			boxSizing: 'border-box',
																			backgroundColor: !isAvailable ? '#f5f5f5' : 'white',
																			color: !isAvailable ? '#999' : 'inherit',
																			outline: 'none',
																			touchAction: 'manipulation',
																		}}
																		onFocus={(e) => {
																			e.target.style.borderColor = '#1976d2';
																			if (e.target.value === '0') e.target.value = '';
																		}}
																		onBlur={(e) => {
																			e.target.style.borderColor = '#ccc';
																			const val = parseInt(e.target.value) || 0;
																			handleQuantityChange(curriculumIndex, book.id, delivery.id, val);
																		}}
																	/>
																</TableCell>
															);
														})}
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
									</Collapse>
								</Box>
								);
							})}
							<Box sx={{
								p: 1,
								backgroundColor: '#fff',
								borderTop: '1px solid #e0e0e0',
								display: 'flex',
								flexDirection: { xs: 'column', md: 'row' },
								justifyContent: 'space-between',
								alignItems: { xs: 'stretch', md: 'center' },
								gap: 1,
								minHeight: '35px',
								flexShrink: 0,
								position: 'sticky',
								bottom: 0,
								zIndex: 1
							}}>
								{!fromOrderConfirm && (
									<Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
										<Button
											variant="outlined"
											sx={{ fontSize: '0.8rem', height: '28px', minWidth: '80px', whiteSpace: 'nowrap' }}
											onClick={() => navigate('/purchase/delivery-datetime', { state: buildDeliveryDateTimeState() })}
										>
											{i18next.t('purchase.go_to_delivery_datetime')}
										</Button>
									</Box>
								)}
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
									<Button
										variant="outlined"
										sx={{ fontSize: '0.8rem', height: '28px', minWidth: '80px' }}
										onClick={() => setIsCancelOrderDialogOpen(true)}
									>
										{fromOrderConfirm ? i18next.t('purchase.cancel_and_return_to_order_confirm') : i18next.t('order.order_cancelled')}
									</Button>
									<Button
										variant="outlined"
										sx={{ fontSize: '0.8rem', height: '28px', minWidth: '80px' }}
										onClick={handleTemporarySave}
									>
										{i18next.t('purchase.save_temporarily')}
									</Button>
									<Button
										variant="contained"
										disabled={selectedCurriculums.length === 0 || getValidSelectedCurriculums().length === 0}
										sx={{
											fontSize: '0.8rem',
											height: '28px',
											minWidth: '80px',
											backgroundColor: 'primary.main',
											'&:hover': { backgroundColor: 'primary.dark' }
										}}
										onClick={handleSubmit}
									>
										{i18next.t('purchase.go_to_order_confirm')}
									</Button>
								</Box>
							</Box>
						</Paper>
					</Box>
				</Grid>
			</Grid>
			<Dialog
				open={curriculumDialogOpen}
				onClose={() => setCurriculumDialogOpen(false)}
				maxWidth="md"
				fullWidth
				PaperProps={{ sx: { maxHeight: '80vh' } }}
			>
				<DialogTitle sx={{ fontSize: '0.9rem', pb: 1 }}>
					{i18next.t('product.curriculum_name')}
				</DialogTitle>
				<DialogContent sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
					{getCategorizedCurriculums.purchasable.length > 0 && (
						<>
							<Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, color: 'primary.main', fontWeight: 'bold' }}>
								{i18next.t('common.purchasable_products')}
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.5, mb: 2 }}>
								{getCategorizedCurriculums.purchasable.map((curriculum) => {
									const isSelected = selectedCurriculums.some(
										selected => selected.name === curriculum.name && selected.type === curriculum.type
									);
									return (
										<Chip
											key={`dialog-${curriculum.type}-${curriculum.name}`}
											label={curriculum.name}
											size="medium"
											color={isSelected ? 'primary' : 'default'}
											variant={isSelected ? 'filled' : 'outlined'}
											onClick={() => handleCurriculumToggle(curriculum.type, curriculum.name)}
											sx={{
												fontSize: '0.8rem',
												cursor: 'pointer',
												maxWidth: '100%',
												'&:hover': { opacity: 0.8 }
											}}
										/>
									);
								})}
							</Box>
						</>
					)}
					{getCategorizedCurriculums.other.length > 0 && (
						<>
							<Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, color: 'text.secondary', fontWeight: 'bold' }}>
								{i18next.t('common.other_products')}
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.5 }}>
								{getCategorizedCurriculums.other.map((curriculum) => (
									<Chip
										key={`dialog-other-${curriculum.type}-${curriculum.name}`}
										label={curriculum.name}
										size="medium"
										variant="outlined"
										disabled
										sx={{
											fontSize: '0.8rem',
											maxWidth: '100%'
										}}
									/>
								))}
							</Box>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
						<Button onClick={handleSelectAllCurriculums} size="small" variant="outlined">
							{i18next.t('common.select_all')}
						</Button>
						<Button onClick={handleDeselectAllCurriculums} size="small" variant="outlined">
							{i18next.t('common.deselect_all')}
						</Button>
					</Box>
					<Button onClick={() => setCurriculumDialogOpen(false)} size="small">
						{i18next.t('common.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={isCancelOrderDialogOpen}
				onClose={() => setIsCancelOrderDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
					{fromOrderConfirm ? i18next.t('purchase.cancel_and_return_to_order_confirm') : i18next.t('order.order_cancelled')}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						{fromOrderConfirm ? i18next.t('purchase.confirm_cancel_add_delivery') : i18next.t('common.confirm_cancel')}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
					<Button onClick={() => setIsCancelOrderDialogOpen(false)} variant="outlined" size="small">
						{i18next.t('common.no')}
					</Button>
					<Box sx={{ flexGrow: 1 }} />
					{tempOrder && (
						<Button onClick={handleCancelOrderWithDeleteTemp} variant="contained" color="error" size="small">
							{i18next.t('purchase.yes_delete_temp_order')}
						</Button>
					)}
					<Button onClick={() => handleCancelOrder()} variant="contained" size="small">
						{i18next.t('common.yes')}
					</Button>
				</DialogActions>
			</Dialog>
			{/* 一時保存データ陳腐化ダイアログ */}
			<Dialog open={tempOrderStaleDialogOpen}>
				<DialogTitle>{i18next.t('purchase.temp_order_stale_title')}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
						{i18next.t('purchase.temp_order_stale_message')}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						variant="contained"
						color="error"
						onClick={async () => {
							try {
								await axios.delete('/api/v1/orders/temp');
							} catch (error) {
								console.error('Failed to delete stale temp order:', error);
							}
							navigate('/purchase');
						}}
					>
						{i18next.t('purchase.delete_temp_and_restart')}
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmDialog
				open={isCompletedSaveTemporaryOrderDialogOpen}
				onClose={null}
				onConfirm={() => setIsCompletedSaveTemporaryOrderDialogOpen(false)}
				title={i18next.t('purchase.save_temporarily')}
				message={i18next.t('purchase.saved_successfully')}
				button1Text={null}
				button2Text={i18next.t('common.confirm')}
			/>
		</Container>
	);
};

export default OrderInput; 